/**
 * LoginPage — the only screen outside AppShell (SEC-01).
 *
 * It borrows the dialog language of DS-43 (dark slate-800 header on a white,
 * slate-400-framed panel) rather than inventing a marketing page: the first
 * thing the user sees should already look like the application they are about
 * to work in.
 */

import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, KeyRound, LogIn, Languages, Stethoscope, User } from 'lucide-react';
import type { CurrentUserDto } from '@nova/shared';
import { ApiError, api } from '@/lib/api';
import { applyLanguage } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { Alert, Button, Card, FormField, Input } from '@/components/ui';

/**
 * Mirrors `ERROR_CODES` in `apps/api/src/common/errors/app-error.ts`. The web app
 * cannot import from the API package, and these two codes are the only ones the
 * login form treats specially — everything else falls back to the server's own
 * Arabic message.
 */
const AUTH_ERROR_CODES = {
  INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  ACCOUNT_DISABLED: 'AUTH_ACCOUNT_DISABLED',
  TOKEN_EXPIRED: 'AUTH_TOKEN_EXPIRED',
} as const;

const HANDLED_CODES: string[] = [
  AUTH_ERROR_CODES.INVALID_CREDENTIALS,
  AUTH_ERROR_CODES.ACCOUNT_DISABLED,
  AUTH_ERROR_CODES.TOKEN_EXPIRED,
];

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '1.0.0';

/**
 * Quick-fill buttons for the accounts created by `apps/api/prisma/seed/02-users.ts`.
 *
 * These are working credentials, so a real clinic's login page must never advertise
 * them: the section renders in dev, or where the deployment has opted in explicitly
 * with `VITE_DEMO_LOGINS=true` at build time. That opt-in is for demo instances only
 * — leave it unset and a production build strips the whole block. Keep this list in
 * step with the seed: a button that fills a password the seed no longer sets is worse
 * than no button at all.
 */
const DEMO_PASSWORD = 'Nova@2026';

/** Build-time, not runtime — Vite inlines the literal so an unset flag drops the JSX. */
const SHOW_DEMO_ACCOUNTS =
  import.meta.env.DEV || import.meta.env.VITE_DEMO_LOGINS === 'true';

const DEMO_ACCOUNTS: { username: string; roleKey: string; tone: string }[] = [
  { username: 'admin', roleKey: 'admin', tone: 'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100' },
  { username: 'reception', roleKey: 'reception', tone: 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100' },
  { username: 'dr.nova', roleKey: 'doctor', tone: 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100' },
  { username: 'dr.omar', roleKey: 'doctor2', tone: 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100' },
  { username: 'noor', roleKey: 'specialist', tone: 'bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100' },
  { username: 'accountant', roleKey: 'accountant', tone: 'bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100' },
];

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: CurrentUserDto;
}

/** Where ProtectedRoute parked the path the user actually asked for. */
interface LoginLocationState {
  from?: { pathname?: string; search?: string };
}

export default function LoginPage() {
  const { t, i18n } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((state) => state.setSession);

  const [revealPassword, setRevealPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const lang: 'AR' | 'EN' = i18n.language.startsWith('ar') ? 'AR' : 'EN';

  // Rebuilt when the language changes so the validation messages follow the
  // toggle in the corner instead of freezing at whatever loaded first.
  const schema = useMemo(
    () =>
      z.object({
        username: z.string().trim().min(1, t('validation.usernameRequired')),
        password: z.string().min(1, t('validation.passwordRequired')),
      }),
    [t],
  );

  type LoginForm = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(schema),
    defaultValues: { username: '', password: '' },
  });

  /** Fill a seeded account's credentials without submitting, so the password
      stays visible for a moment and a wrong pick is easy to correct. */
  function fillDemoAccount(username: string): void {
    setValue('username', username, { shouldValidate: true, shouldDirty: true });
    setValue('password', DEMO_PASSWORD, { shouldValidate: true, shouldDirty: true });
    setSubmitError(null);
  }

  const from = (location.state as LoginLocationState | null)?.from;
  // Bouncing back to /login would trap the user in a loop after a successful sign-in.
  const redirectTo =
    from?.pathname && !from.pathname.startsWith('/login')
      ? `${from.pathname}${from.search ?? ''}`
      : '/dashboard';

  function describeFailure(error: unknown): string {
    if (error instanceof ApiError) {
      if (HANDLED_CODES.includes(error.code)) return t(`errors.${error.code}`);
      if (error.code === 'NETWORK_ERROR' || error.code === 'TIMEOUT') return t('errors.network');
      // The API states the real reason (locked, expired password…) better than a
      // generic client string can.
      return error.localized(lang);
    }
    return t('errors.unknown');
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    setPending(true);
    try {
      const { data } = await api.post<LoginResponse>('/auth/login', values);
      setSession({ accessToken: data.accessToken, refreshToken: data.refreshToken }, data.user);
      // NFR-03 — the session opens in the user's own language, not the one the
      // login screen happened to be showing.
      applyLanguage(data.user.language);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      setSubmitError(describeFailure(error));
      setPending(false);
    }
  });

  return (
    <div className="relative flex h-screen w-full items-center justify-center bg-slate-100 p-4">
      {/* NFR-03/04 — the language is switchable before anyone has a session. */}
      <div className="absolute top-3 end-3">
        <Button
          variant="secondary"
          size="sm"
          icon={Languages}
          data-testid="login-language-toggle"
          data-next-lang={lang === 'AR' ? 'EN' : 'AR'}
          title={t('language.label')}
          onClick={() => applyLanguage(lang === 'AR' ? 'EN' : 'AR')}
        >
          {t('language.switchTo')}
        </Button>
      </div>

      <div className="w-full max-w-sm">
        {/* DS-43 — dialog frame: slate-400 border, dark header, white body. */}
        <Card className="overflow-hidden border-slate-400 shadow-2xl">
          <header className="flex items-center gap-2.5 bg-slate-800 px-4 py-3 text-white">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-blue-600">
              <Stethoscope className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-sm font-bold leading-tight">{t('common:app.name')}</h1>
              <p className="truncate text-[10px] uppercase tracking-widest text-slate-400">
                {t('login.subtitle')}
              </p>
            </div>
          </header>

          <form onSubmit={onSubmit} className="flex flex-col gap-3 p-4" noValidate>
            <div>
              <h2 className="text-xs font-bold text-slate-700">{t('login.heading')}</h2>
              <p className="mt-0.5 text-[11px] leading-tight text-slate-500">{t('login.hint')}</p>
            </div>

            {submitError ? <Alert tone="danger" data-testid="login-error">{submitError}</Alert> : null}

            <FormField label={t('login.username')} required error={errors.username?.message}>
              <Input
                icon={User}
                // Credentials are ASCII even in an Arabic UI — typing them RTL
                // puts the caret in the wrong place.
                dir="ltr"
                autoComplete="username"
                autoFocus
                spellCheck={false}
                autoCapitalize="none"
                placeholder={t('login.usernamePlaceholder')}
                data-testid="login-username"
                {...register('username')}
              />
            </FormField>

            <FormField label={t('login.password')} required error={errors.password?.message}>
              <Input
                icon={KeyRound}
                dir="ltr"
                type={revealPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder={t('login.passwordPlaceholder')}
                data-testid="login-password"
                suffix={
                  <button
                    type="button"
                    onClick={() => setRevealPassword((current) => !current)}
                    title={revealPassword ? t('login.hidePassword') : t('login.showPassword')}
                    aria-label={revealPassword ? t('login.hidePassword') : t('login.showPassword')}
                    aria-pressed={revealPassword}
                    // DS-54 — exempt from the tablet 44px rule; it lives inside a field.
                    className="no-touch-size rounded p-0.5 text-slate-400 transition hover:bg-slate-200 hover:text-slate-700"
                  >
                    {revealPassword ? (
                      <EyeOff className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <Eye className="h-3.5 w-3.5" aria-hidden />
                    )}
                  </button>
                }
                {...register('password')}
              />
            </FormField>

            <Button type="submit" data-testid="login-submit" icon={LogIn} loading={pending} fullWidth className="mt-1">
              {pending ? t('login.submitting') : t('login.submit')}
            </Button>

            {SHOW_DEMO_ACCOUNTS ? (
              <section className="mt-1 border-t border-slate-200 pt-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="label-micro">{t('demo.title')}</span>
                  <span className="text-[10px] text-slate-400">{t('demo.hint')}</span>
                </div>

                <div className="mt-2 grid grid-cols-3 max-lg:grid-cols-2 max-sm:grid-cols-1 gap-1.5">
                  {DEMO_ACCOUNTS.map((account) => (
                    <button
                      key={account.username}
                      type="button"
                      onClick={() => fillDemoAccount(account.username)}
                      title={t('demo.fillTitle', {
                        role: t(`demo.roles.${account.roleKey}`),
                        username: account.username,
                      })}
                      className={`flex flex-col items-center gap-0.5 rounded border px-1.5 py-1.5 transition ${account.tone}`}
                    >
                      <span className="text-[11px] font-bold leading-tight">
                        {t(`demo.roles.${account.roleKey}`)}
                      </span>
                      {/* Usernames are ASCII data, not copy — they stay LTR (DS-51). */}
                      <span className="ltr-num truncate font-mono text-[9px] opacity-70">
                        {account.username}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
          </form>
        </Card>

        <footer className="mt-3 flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400">
          <span>{t('footer.version')}</span>
          {/* DS-51 — the version number stays LTR inside Arabic copy. */}
          <span className="ltr-num font-mono">{APP_VERSION}</span>
        </footer>
      </div>
    </div>
  );
}
